
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getTransactionsByUserId, getFamilyMembersByFamilyId, createTransfer, getTransfersByUserId } from "@/lib/firebase";
import { Transaction, User, Transfer } from "@/types";
import { useToast } from "@/hooks/use-toast";

export function useChildViewData() {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [canTransfer, setCanTransfer] = useState(false);
  const [transfersError, setTransfersError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.id || !currentUser?.familyId) return;
      
      setIsLoading(true);
      try {
        console.log("ChildView: Fetching data for user:", currentUser.id);
        
        // Fetch transactions (this should always work)
        const transactionsData = await getTransactionsByUserId(currentUser.id);
        console.log("ChildView: Transactions fetched successfully");
        setTransactions(transactionsData as Transaction[]);
        
        // Try to fetch transfers, but don't fail if there are permission issues
        try {
          console.log("ChildView: Fetching transfers for user:", currentUser.id);
          const transfersData = await getTransfersByUserId(currentUser.id);
          console.log("ChildView: Transfers fetched successfully");
          setTransfers(transfersData as Transfer[]);
          setTransfersError(null);
        } catch (transferError: any) {
          console.warn("ChildView: Could not fetch transfers (continuing without transfer data):", transferError);
          setTransfersError(transferError.message || "Failed to load transfers");
          setTransfers([]);
        }
        
        // Try to fetch family members for transfers
        try {
          console.log("ChildView: Fetching family members for family:", currentUser.familyId);
          const familyMembersData = await getFamilyMembersByFamilyId(currentUser.familyId);
          console.log("ChildView: Family members fetched successfully:", familyMembersData.length);
          setFamilyMembers(familyMembersData);
          setCanTransfer(true);
        } catch (familyError) {
          console.warn("ChildView: Could not fetch family members (transfer functionality disabled):", familyError);
          setCanTransfer(false);
        }
        
      } catch (error) {
        console.error("ChildView: Error fetching core data:", error);
        toast({
          title: "Error",
          description: "Failed to load data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, toast]);

  const handleTransfer = async (toUserId: string, amount: number, description: string) => {
    if (!currentUser?.id) return;
    
    try {
      await createTransfer(currentUser.id, toUserId, amount, description);
      
      // Try to refresh transfers, but don't fail if there are issues
      try {
        const transfersData = await getTransfersByUserId(currentUser.id);
        setTransfers(transfersData as Transfer[]);
        setTransfersError(null);
      } catch (transferError: any) {
        console.warn("Could not refresh transfers after creating new transfer:", transferError);
        setTransfersError(transferError.message || "Failed to refresh transfers");
      }
      
      toast({
        title: "Success",
        description: "Transfer request submitted! Waiting for parent approval.",
      });
    } catch (error) {
      console.error("Transfer error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Transfer request failed. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    transactions,
    transfers,
    familyMembers,
    isLoading,
    canTransfer,
    transfersError,
    handleTransfer
  };
}
